import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from "react";
import { AnnotationService } from "./annotation.service";

export interface AnnotationEventsContextValue {
	onAnnotationsChange?: (instantJson: InstantJSON) => void;
	onDrawingStart?: () => void;
	onDrawingEnd?: () => void;
	onError?: (error: string) => void;
}

const AnnotationEventsContext = createContext<AnnotationEventsContextValue>({});

export interface AnnotationEventsProviderProps
	extends PropsWithChildren<AnnotationEventsContextValue> {
	/** The current Korrektur step id */
	korrekturStepId: string;
	/** The current Korrektur student klausur id */
	korrekturStudentKlausurId: string;
}

/**
 *
 * @param children - The children to render
 * @param onAnnotationsChange - Callback function when the annotations change
 * @param onDrawingStart - Callback function when the drawing starts
 * @param onDrawingEnd - Callback function when the drawing ends
 * @param onError - Callback function when an error occurs
 */
export function AnnotationEventsProvider({
	children,
	onAnnotationsChange,
	onDrawingStart,
	onDrawingEnd,
	onError,
	korrekturStepId,
	korrekturStudentKlausurId,
}: AnnotationEventsProviderProps) {
	const value = useMemo<AnnotationEventsContextValue>(() => {
		const normalizePdfId = (instantJson: InstantJSON): InstantJSON => {
			const pdfId = (instantJson as { pdfId?: unknown }).pdfId;
			if (
				pdfId &&
				typeof pdfId === "object" &&
				typeof (pdfId as { changing?: unknown }).changing === "string" &&
				typeof (pdfId as { permanent?: unknown }).permanent === "string"
			) {
				return instantJson;
			}

			// Drop invalid pdfId shapes from some Nutrient exports.
			const { pdfId: _ignored, ...rest } = instantJson as InstantJSON & {
				pdfId?: unknown;
			};
			return rest;
		};

		const isForeignAnnotation = (a: InstantJSONAnnotation): boolean => {
			const customData = (a as { customData?: unknown }).customData;
			if (!customData || typeof customData !== "object") return false;
			return (
				(customData as { __source?: unknown }).__source === "otherKorrekturStep"
			);
		};

		// Prevent saving "foreign" annotations (from other korrektur steps) into the
		// current user's annotation state.
		const stripForeignAnnotations = (instantJson: InstantJSON): InstantJSON => {
			const filtered = (instantJson.annotations ?? []).filter(
				(a) => !isForeignAnnotation(a),
			);
			return normalizePdfId({
				...instantJson,
				annotations: filtered,
			});
		};

		const composedOnError = (message: string) => {
			try {
				onError?.(message);
			} catch (err) {
				// Fall back to console to avoid crashing event flow
				console.error("AnnotationEvents onError handler failed:", err);
			}
		};

		/**
		 * This functions updates the annotations for the korrektur
		 */
		const composedOnAnnotationsChange = async (instantJson: InstantJSON) => {
			try {
				// Extract modified page index if provided by the viewer hook
				const pageIndex = (instantJson as { __modifiedPageIndex?: number })
					.__modifiedPageIndex as number;

				if (pageIndex === null || pageIndex === undefined) {
					throw new Error("Page index is null");
				}

				const payload = stripForeignAnnotations(instantJson);
				// we dont want to save annotations from other annotators

				await AnnotationService.getInstance().updateUserPdfAnnotations(
					korrekturStepId,
					korrekturStudentKlausurId,
					payload,
					pageIndex,
				);
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Failed to save annotations";
				composedOnError(message);
			}

			try {
				onAnnotationsChange?.(instantJson);
			} catch (err) {
				console.error(
					"AnnotationEvents onAnnotationsChange handler failed:",
					err,
				);
			}
		};

		return {
			onAnnotationsChange: composedOnAnnotationsChange,
			onDrawingStart,
			onDrawingEnd,
			onError: composedOnError,
		};
	}, [
		onAnnotationsChange,
		onDrawingStart,
		onDrawingEnd,
		onError,
		korrekturStepId,
		korrekturStudentKlausurId,
	]);

	return (
		<AnnotationEventsContext.Provider value={value}>
			{children}
		</AnnotationEventsContext.Provider>
	);
}

export function useAnnotationEvents(): AnnotationEventsContextValue {
	return useContext(AnnotationEventsContext);
}
